import { useState, useCallback, useEffect, useRef } from 'react';
import './BogglePuzzle.css';

interface BogglePuzzleProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  targetScore: number;
  timeLimit: number; // en secondes
}

// Mots thématiques courts qu'on peut placer dans la grille
const PLACEABLE_WORDS_SHORT = [
  // 3 lettres - faciles à placer
  'ami', 'vie', 'foi', 'but', 'rue', 'oui', 'eau', 'air', 'jeu', 'bon',
  // 4 lettres - éducation et univers
  'aide', 'aime', 'etre', 'voir', 'dire', 'bien', 'soin', 'lien', 'paix', 'joie',
  'peur', 'tous', 'jour', 'sens', 'fort', 'velo', 'code', 'voix', 'rire', 'gens',
  'educ', 'calme', 'noir', 'vent', 'pont', 'role', 'plan', 'site', 'mode',
  // 5 lettres - thématiques
  'aider', 'guide', 'brave', 'coeur', 'force', 'monde', 'ecole',
  'ecoute', 'valeur', 'espoir', 'radio', 'onze', 'parti', 'temps',
];

// Mots LONGS (6-8 lettres) qu'on essaie de placer en priorité
const PLACEABLE_WORDS_LONG = [
  // 6 lettres - BEAUCOUP PLUS
  'amitie', 'equipe', 'ecoute', 'enfant', 'espoir', 'calmer', 'parler',
  'aiment', 'entier', 'sortir', 'partir', 'venir', 'tenir', 'sentir',
  'donner', 'porter', 'mettre', 'rester', 'tomber', 'passer', 'entrer',
  'monter', 'courir', 'dormir', 'servir', 'vivre', 'suivre', 'ouvrir',
  'finir', 'saisir', 'battre', 'mordre', 'perdre', 'rendre', 'vendre',
  'valoir', 'savoir', 'devoir', 'mourir', 'offrir', 'couvrir',
  // 7 lettres
  'confier', 'sourire', 'emotion', 'partage', 'courage', 'famille',
  'respect', 'eduquer', 'ecouter', 'partager', 'trouver', 'chercher',
  'demander', 'regarder', 'entendre', 'attendre', 'montrer', 'sembler',
  'devenir', 'revenir', 'obtenir', 'soutenir', 'souvenir', 'contenir',
];

// Lettres fréquentes en français pour remplir les cases vides
const COMMON_VOWELS = ['E', 'E', 'E', 'A', 'A', 'I', 'I', 'O', 'U'];
const COMMON_CONSONANTS = ['S', 'S', 'R', 'R', 'T', 'T', 'N', 'N', 'L', 'D', 'C', 'M', 'P', 'V', 'B', 'F', 'G'];

// Dictionnaire de base + mots thématiques (éducation spécialisée, univers du jeu)
const THEMATIC_WORDS = new Set([
  // ===== ÉDUCATION SPÉCIALISÉE - CHAMP LEXICAL COMPLET =====
  // Métier et rôles (avec pluriels et féminins)
  'educ', 'educs', 'educateur', 'educateurs', 'educatrice', 'educatrices',
  'aide', 'aides', 'aider', 'aidant', 'aidants', 'aidante', 'aidantes',
  'tuteur', 'tuteurs', 'tutrice', 'tutrices',
  'animateur', 'animateurs', 'animatrice', 'animatrices',
  'referent', 'referents', 'referente', 'referentes',
  // Relations et liens (avec pluriels)
  'ami', 'amie', 'amis', 'amies', 'amitie', 'amities',
  'lien', 'liens', 'relation', 'relations', 'confiance', 'confiances',
  'ecoute', 'ecoutes', 'dialogue', 'dialogues', 'parole', 'paroles',
  'parler', 'dire', 'entendre', 'voir',
  // Émotions et sentiments (avec pluriels et féminins)
  'emotion', 'emotions', 'empathie', 'empathies', 'coeur', 'coeurs',
  'joie', 'joies', 'peur', 'peurs', 'colere', 'coleres',
  'tristesse', 'tristesses', 'calme', 'calmes',
  'serein', 'sereins', 'sereine', 'sereines',
  'heureux', 'heureuse', 'heureuses',
  // Valeurs éducatives (avec pluriels)
  'respect', 'respects', 'patience', 'patiences', 'partage', 'partages',
  'entraide', 'entraides', 'solidarite', 'solidarites', 'tolerance', 'tolerances',
  'bienveillance', 'bienveillances', 'attention', 'attentions',
  'soin', 'soins', 'soutien', 'soutiens',
  'tendresse', 'tendresses', 'douceur', 'douceurs',
  'gentillesse', 'gentillesses', 'bonté', 'bonte', 'bontes',
  // Actions éducatives
  'accompagner', 'guider', 'guide', 'guides', 'aider', 'soutenir', 'ecouter',
  'observer', 'comprendre', 'expliquer', 'montrer', 'apprendre', 'enseigner',
  'jouer', 'creer', 'imaginer', 'rever', 'grandir', 'evoluer', 'progresser',
  // Espaces et temps (avec pluriels)
  'groupe', 'groupes', 'equipe', 'equipes', 'reunion', 'reunions',
  'atelier', 'ateliers', 'activite', 'activites', 'projet', 'projets',
  'objectif', 'objectifs', 'temps', 'moment', 'moments', 'instant', 'instants',
  'jour', 'jours', 'quotidien', 'quotidiens', 'routine', 'routines', 'rythme', 'rythmes',
  // Concepts clés (avec pluriels)
  'autonomie', 'autonomies', 'independance', 'independances', 'liberte', 'libertes',
  'choix', 'decision', 'decisions', 'responsabilite', 'responsabilites',
  'estime', 'estimes', 'confiance', 'confiances', 'securite', 'securites',
  'cadre', 'cadres', 'limite', 'limites', 'regle', 'regles',
  // Qualités (avec pluriels et féminins)
  'force', 'forces', 'courage', 'courages', 'brave', 'braves',
  'fort', 'forts', 'forte', 'fortes', 'capable', 'capables',
  'competent', 'competents', 'competente', 'competentes',
  'creatif', 'creatifs', 'creative', 'creatives',
  'curieux', 'curieuse', 'curieuses',
  'ouvert', 'ouverts', 'ouverte', 'ouvertes',
  // Bien-être (avec pluriels)
  'bien', 'biens', 'sante', 'santes', 'corps', 'esprit', 'esprits',
  'repos', 'detente', 'detentes', 'pause', 'pauses', 'calme', 'calmes',
  'paix', 'harmonie', 'harmonies', 'equilibre', 'equilibres', 'stabilite', 'stabilites',
  // Termes professionnels courants (avec pluriels)
  'entretien', 'entretiens', 'reunion', 'reunions', 'synthese', 'syntheses',
  'bilan', 'bilans', 'evaluation', 'evaluations', 'suivi', 'suivis',
  'parcours', 'trajet', 'trajets', 'chemin', 'chemins', 'etape', 'etapes',
  'but', 'buts', 'sens', 'valeur', 'valeurs',

  // ===== UNIVERS STRANGER THINGS / HAWKINS =====
  'velo', 'velos', 'monde', 'mondes', 'faille', 'failles', 'rift', 'rifts',
  'onze', 'eleven', 'parti', 'partis', 'party', 'partys',
  'radio', 'radios', 'walkie', 'walkies', 'morse', 'code', 'codes',
  'signal', 'signaux', 'lumiere', 'lumieres',
  'noir', 'noirs', 'noire', 'noires', 'ombre', 'ombres',
  'portail', 'portails', 'dimension', 'dimensions', 'upside', 'down',
  'monstre', 'monstres', 'hero', 'heros', 'heroine', 'heroines',
  'dustin', 'erica', 'lucas', 'mike', 'will', 'max', 'nancy', 'steve',

  // ===== MOTS BONUS COMBAT =====
  'battre', 'combat', 'combats', 'vaincre', 'victoire', 'victoires',
  'gagner', 'win', 'fight', 'fights',
  'pouvoir', 'pouvoirs', 'magie', 'magies', 'energie', 'energies',
  'attaque', 'attaques', 'defense', 'defenses',
]);

// Dictionnaire français complet (mots courts et moyens communs) avec pluriels et féminins
const FRENCH_DICTIONARY = new Set([
  // 2 lettres (pas de points mais validés)
  'de', 'du', 'le', 'la', 'un', 'je', 'tu', 'il', 'on', 'ce', 'se', 'ne', 'ou', 'et', 'en', 'au', 'si', 'ni', 'ma', 'ta', 'sa', 'me', 'te', 'va', 'eu', 'pu', 'su', 'lu', 'bu', 'nu', 'mu', 'os', 'us',
  // 3 lettres (avec pluriels)
  'les', 'des', 'une', 'uns', 'par', 'sur', 'que', 'qui', 'son', 'ses', 'aux', 'ces', 'mes', 'tes', 'nos', 'vos', 'lui', 'eux',
  'peu', 'vie', 'vies', 'ans', 'eau', 'eaux', 'air', 'airs', 'rue', 'rues', 'bas', 'mot', 'mots', 'fin', 'fins', 'cas',
  'foi', 'fois', 'loi', 'lois', 'dos', 'fil', 'fils', 'fer', 'fers', 'sud', 'est', 'ici',
  'bon', 'bons', 'mal', 'maux', 'art', 'arts', 'nez', 'oui', 'non', 'ami', 'amis', 'amie', 'amies',
  'but', 'buts', 'jeu', 'jeux', 'feu', 'feux', 'cou', 'cous', 'bec', 'becs', 'lit', 'lits',
  'roi', 'rois', 'sac', 'sacs', 'ton', 'tons', 'vin', 'vins', 'net', 'nets', 'nette', 'nettes',
  'gaz', 'sec', 'secs', 'seche', 'seches', 'dur', 'durs', 'dure', 'dures',
  'pur', 'purs', 'pure', 'pures', 'mur', 'murs', 'sol', 'sols', 'sel', 'sels',
  'cle', 'cles', 'pre', 'pres', 'dix', 'six', 'etc', 'age', 'ages',
  'ile', 'iles', 'arc', 'arcs', 'bar', 'bars', 'cap', 'caps', 'col', 'cols',
  'cor', 'cors', 'dot', 'dots', 'duc', 'ducs', 'ego', 'fan', 'fans', 'gel', 'gels',
  'gre', 'hop', 'jus', 'lac', 'lacs', 'las', 'mac', 'mat', 'mats', 'mate', 'mates',
  'nid', 'nids', 'noix', 'ode', 'odes', 'orge', 'pan', 'pans', 'pic', 'pics',
  'pie', 'pies', 'pin', 'pins', 'pot', 'pots', 'pou', 'poux', 'pro', 'pros',
  'ras', 'rat', 'rats', 'raz', 'rit', 'rot', 'rots', 'sas', 'soi', 'sou', 'sous',
  'sue', 'tas', 'tau', 'the', 'thes', 'tic', 'tics', 'tir', 'tirs', 'toi',
  'tri', 'tris', 'uni', 'unis', 'unie', 'unies', 'ure', 'van', 'vans',
  'ver', 'vers', 'vif', 'vifs', 'vive', 'vives', 'vis', 'vit', 'vol', 'vols', 'vue', 'vues', 'zoo', 'zoos',
  // 4 lettres - TRÈS ENRICHI (avec pluriels et féminins)
  'avec', 'pour', 'dans', 'plus', 'vous', 'nous', 'tout', 'tous', 'toute', 'toutes',
  'fait', 'faits', 'faite', 'faites', 'bien', 'biens', 'mais', 'sans', 'elle', 'elles',
  'dont', 'deux', 'tres', 'quel', 'quels', 'quelle', 'quelles', 'leur', 'leurs', 'meme', 'memes',
  'dire', 'voir', 'rien', 'jour', 'jours', 'part', 'parts', 'pays', 'main', 'mains',
  'chef', 'chefs', 'coup', 'coups', 'aide', 'aides', 'idee', 'idees', 'lieu', 'lieux',
  'sens', 'fort', 'forts', 'forte', 'fortes', 'seul', 'seuls', 'seule', 'seules',
  'soit', 'pris', 'prise', 'prises', 'mise', 'mises', 'vote', 'votes', 'prix',
  'base', 'bases', 'euro', 'euros', 'zone', 'zones', 'plan', 'plans', 'deja',
  'cafe', 'cafes', 'film', 'films', 'role', 'roles', 'gens', 'mode', 'modes',
  'code', 'codes', 'type', 'types', 'site', 'sites', 'haut', 'hauts', 'haute', 'hautes',
  'mort', 'morts', 'morte', 'mortes', 'tour', 'tours', 'noir', 'noirs', 'noire', 'noires',
  'long', 'longs', 'longue', 'longues', 'tard', 'face', 'faces', 'date', 'dates',
  'mere', 'meres', 'pere', 'peres', 'fille', 'filles', 'peur', 'peurs',
  'soif', 'faim', 'vent', 'vents', 'bois', 'pont', 'ponts', 'gare', 'gares',
  'port', 'ports', 'mois', 'nuit', 'nuits', 'voie', 'voies', 'bord', 'bords',
  'cote', 'cotes', 'fond', 'fonds', 'nord', 'paix', 'velo', 'velos', 'lien', 'liens',
  'soin', 'soins', 'voix', 'rire', 'rires', 'aime', 'aimes', 'aimee', 'aimees',
  // Verbes conjugués et mots 4 lettres - TRÈS ENRICHI
  'aider', 'aller', 'allez', 'avoir', 'avez', 'bout', 'bouts', 'cite', 'cites', 'citee', 'citees',
  'clos', 'close', 'closes', 'cola', 'colas', 'cole', 'coule', 'cour', 'cours',
  'cree', 'crees', 'creee', 'creees', 'crie', 'cuit', 'cuits', 'cuite', 'cuites',
  'cure', 'cures', 'dine', 'dise', 'doit', 'dome', 'domes', 'dort', 'dose', 'doses',
  'dote', 'dotes', 'dotee', 'dotees', 'emet', 'fais', 'fier', 'fiers', 'fiere', 'fieres',
  'file', 'files', 'fini', 'finis', 'finie', 'finies', 'fixe', 'fixes', 'fixee', 'fixees',
  'fuir', 'fuit', 'fume', 'fumes', 'fumee', 'fumees', 'gate', 'gates', 'gatee', 'gatees',
  'gele', 'geles', 'gelee', 'gelees', 'gere', 'geres', 'geree', 'gerees', 'gout', 'gouts',
  'hait', 'hier', 'joue', 'joues', 'jouee', 'jouees', 'jure', 'jures', 'juree', 'jurees',
  'lace', 'laces', 'lacee', 'lacees', 'lave', 'laves', 'lavee', 'lavees',
  'lent', 'lents', 'lente', 'lentes', 'leve', 'leves', 'levee', 'levees',
  'lier', 'lies', 'liee', 'liees', 'lire', 'loge', 'loges', 'logee', 'logees',
  'loue', 'loues', 'louee', 'louees', 'luit', 'lune', 'lunes',
  'male', 'males', 'mare', 'mares', 'mele', 'meles', 'melee', 'melees',
  'ment', 'meut', 'mine', 'mines', 'minee', 'minees', 'moue', 'moues',
  'mure', 'mures', 'muree', 'murees', 'mute', 'mutes', 'mutee', 'mutees',
  'nage', 'nages', 'nagee', 'nagees', 'note', 'notes', 'notee', 'notees',
  'noue', 'noues', 'nouee', 'nouees', 'orne', 'ornes', 'ornee', 'ornees',
  'oser', 'oses', 'osee', 'osees', 'oter', 'pale', 'pales', 'pare', 'pares', 'paree', 'parees',
  'pave', 'paves', 'pavee', 'pavees', 'paie', 'perd', 'pete', 'pile', 'piles',
  'plie', 'plies', 'pliee', 'pliees', 'peut', 'plut',
  'pose', 'poses', 'posee', 'posees', 'pote', 'potes', 'pouf', 'poufs',
  'prie', 'pries', 'priee', 'priees', 'rage', 'rages', 'rame', 'rames', 'ramee', 'ramees',
  'rase', 'rases', 'rasee', 'rasees', 'rate', 'rates', 'ratee', 'ratees',
  'rave', 'raves', 'ravi', 'ravis', 'ravie', 'ravies',
  'recu', 'recus', 'recue', 'recues', 'reel', 'reels', 'reelle', 'reelles',
  'reme', 'rend', 'repu', 'repus', 'repue', 'repues',
  'rime', 'rimes', 'rimee', 'rimees', 'rite', 'rites', 'rive', 'rives',
  'rode', 'rodes', 'rodee', 'rodees', 'rond', 'ronds', 'ronde', 'rondes',
  'roue', 'roues', 'rouee', 'rouees', 'rude', 'rudes', 'ruee', 'ruees',
  'sale', 'sales', 'salee', 'salees', 'scie', 'scies', 'sciee', 'sciees',
  'seme', 'semes', 'semee', 'semees', 'sera', 'sert', 'sied',
  'soir', 'soirs', 'sole', 'soles', 'sont', 'sort', 'sorts',
  'suce', 'suces', 'sucee', 'sucees', 'suie', 'suies', 'suit',
  'sure', 'surs', 'sures', 'tact', 'taie', 'taies',
  'tape', 'tapes', 'tapee', 'tapees', 'tare', 'tares', 'taree', 'tarees',
  'tari', 'taris', 'tarie', 'taries', 'taxe', 'taxes', 'taxee', 'taxees',
  'tels', 'telle', 'telles', 'tend', 'tenu', 'tenus', 'tenue', 'tenues',
  'test', 'tests', 'tete', 'tetes', 'tien', 'tiens', 'tienne', 'tiennes',
  'tire', 'tires', 'tiree', 'tirees', 'toge', 'toges', 'toit', 'toits',
  'tome', 'tomes', 'tond', 'tort', 'torts', 'toue', 'toues', 'touee', 'touees',
  'tuer', 'tues', 'tuee', 'tuees', 'turc', 'turcs', 'turque', 'turques',
  'user', 'uses', 'usee', 'usees', 'vaut', 'vecu', 'vecus', 'vecue', 'vecues',
  'vend', 'venu', 'venus', 'venue', 'venues', 'veto',
  'veuf', 'veufs', 'veuve', 'veuves', 'veux', 'vice', 'vices',
  'vide', 'vides', 'videe', 'videes', 'vies', 'vile', 'vils', 'viles',
  'visa', 'visas', 'vise', 'vises', 'visee', 'visees', 'vite',
  'voeu', 'voeux', 'voir', 'voit', 'vole', 'voles', 'volee', 'volees',
  'volt', 'volts', 'vomi', 'vomis', 'vomie', 'vomies', 'zinc', 'zincs',
  // Noms et adjectifs 4 lettres supplémentaires (avec pluriels et féminins)
  'acte', 'actes', 'aise', 'aises', 'aisee', 'aisees',
  'amer', 'amers', 'amere', 'ameres', 'arme', 'armes', 'armee', 'armees',
  'asie', 'aube', 'aubes', 'auto', 'autos', 'banc', 'bancs',
  'beau', 'beaux', 'belle', 'belles', 'bebe', 'bebes', 'bete', 'betes',
  'beur', 'beurs', 'bile', 'biles', 'bleu', 'bleus', 'bleue', 'bleues',
  'bloc', 'blocs', 'bras', 'brie', 'bries', 'brun', 'bruns', 'brune', 'brunes',
  'buee', 'buees', 'cage', 'cages', 'cake', 'cakes', 'calme', 'calmes',
  'cape', 'capes', 'cave', 'caves', 'cela', 'cent', 'cents', 'ceux',
  'char', 'chars', 'chat', 'chats', 'chatte', 'chattes',
  'cher', 'chers', 'chere', 'cheres', 'ciel', 'cieux',
  'clef', 'clefs', 'coco', 'cocos', 'coma', 'comas', 'cone', 'cones', 'coq', 'coqs',
  'cran', 'crans', 'creu', 'creux', 'creuse', 'creuses',
  'dame', 'dames', 'dard', 'dards', 'deco', 'decos', 'dent', 'dents',
  'dieu', 'dieux', 'donc', 'dore', 'dores', 'doree', 'dorees',
  'doux', 'douce', 'douces', 'drap', 'draps', 'echo', 'echos',
  'eden', 'edit', 'edits', 'elue', 'elus', 'elues', 'epee', 'epees',
  'fade', 'fades', 'fard', 'fards', 'faux', 'fausse', 'fausses',
  'fete', 'fetes', 'feve', 'feves', 'fine', 'fins', 'fines',
  'flot', 'flots', 'flux', 'foin', 'foins', 'foot', 'foots',
  'four', 'fours', 'froc', 'frocs', 'gala', 'galas', 'gang', 'gangs',
  'geai', 'geais', 'gene', 'genes', 'genee', 'genees', 'gite', 'gites',
  'golf', 'golfs', 'gras', 'grasse', 'grasses', 'gris', 'grise', 'grises',
  'gros', 'grosse', 'grosses', 'herbe', 'herbes', 'heros', 'heure', 'heures',
  'hiver', 'hivers', 'homme', 'hommes', 'huile', 'huiles',
  'joli', 'jolis', 'jolie', 'jolies', 'juge', 'juges', 'jugee', 'jugees',
  'jupe', 'jupes', 'jute', 'jutes', 'kaki', 'kakis', 'kilo', 'kilos',
  'lait', 'laits', 'laid', 'laids', 'laide', 'laides', 'lame', 'lames',
  'lard', 'lards', 'lime', 'limes', 'lion', 'lions', 'lionne', 'lionnes',
  'lite', 'lites', 'lobe', 'lobes', 'loin', 'loto', 'lotos',
  'loud', 'louds', 'lourde', 'lourdes', 'loup', 'loups', 'louve', 'louves',
  'luxe', 'luxes', 'malt', 'malts', 'mars',
  'melon', 'melons', 'menu', 'menus', 'menue', 'menues', 'miel', 'miels',
  'mont', 'monts', 'moto', 'motos', 'muet', 'muets', 'muette', 'muettes',
  'nain', 'nains', 'naine', 'naines', 'neon', 'neons', 'nerf', 'nerfs',
  'neuf', 'neufs', 'neuve', 'neuves', 'noce', 'noces', 'onde', 'ondes',
  'opus', 'oral', 'oraux', 'orale', 'orales', 'ours',
  'page', 'pages', 'pair', 'pairs', 'paire', 'paires', 'pari', 'paris',
  'pate', 'pates', 'patee', 'patees', 'peau', 'peaux', 'pied', 'pieds',
  'pipe', 'pipes', 'pire', 'pires', 'piste', 'pistes', 'plat', 'plats', 'plate', 'plates',
  'pneu', 'pneus', 'poil', 'poils', 'pois', 'pole', 'poles',
  'porc', 'porcs', 'prof', 'profs', 'puce', 'puces', 'puis',
  'quai', 'quais', 'race', 'races', 'raid', 'raids', 'rail', 'rails',
  'rang', 'rangs', 'rare', 'rares', 'rayon', 'rayons', 'rein', 'reins',
  'reve', 'reves', 'revee', 'revees', 'ring', 'rings', 'robe', 'robes',
  'rock', 'rocks', 'rose', 'roses', 'rosee', 'rosees',
  'sain', 'sains', 'saine', 'saines', 'sang', 'sangs', 'sauf', 'saufs', 'sauve', 'sauves',
  'saut', 'sauts', 'seau', 'seaux', 'sida', 'soda', 'sodas',
  'soie', 'soies', 'souk', 'souks', 'star', 'stars', 'stop', 'stops',
  'tank', 'tanks', 'tapi', 'tapis', 'tapie', 'tapies', 'taux',
  'taxi', 'taxis', 'tele', 'teles', 'tige', 'tiges', 'toux',
  'trac', 'tracs', 'tram', 'trams', 'truc', 'trucs', 'tube', 'tubes',
  'urne', 'urnes', 'vain', 'vains', 'vaine', 'vaines', 'vase', 'vases',
  'veau', 'veaux', 'vert', 'verts', 'verte', 'vertes',
  'vieux', 'vieille', 'vieilles', 'vrai', 'vrais', 'vraie', 'vraies', 'zoom', 'zooms',
  // 5 lettres - ENRICHI avec pluriels et féminins
  'autre', 'autres', 'comme', 'entre', 'avoir', 'aussi', 'apres', 'avant',
  'monde', 'mondes', 'selon', 'grace', 'graces',
  'grand', 'grands', 'grande', 'grandes', 'petit', 'petits', 'petite', 'petites',
  'jeune', 'jeunes', 'vieille', 'vieilles', 'homme', 'hommes', 'femme', 'femmes',
  'chose', 'choses', 'point', 'points', 'temps', 'annee', 'annees',
  'enfant', 'enfants', 'place', 'places', 'force', 'forces',
  'effet', 'effets', 'forme', 'formes', 'ordre', 'ordres',
  'droit', 'droits', 'droite', 'droites', 'parti', 'partis', 'partie', 'parties',
  'titre', 'titres', 'suite', 'suites', 'ligne', 'lignes',
  'terre', 'terres', 'table', 'tables', 'porte', 'portes',
  'route', 'routes', 'scene', 'scenes', 'image', 'images',
  'livre', 'livres', 'debut', 'debuts', 'matin', 'matins',
  'soire', 'soiree', 'soirees', 'reste', 'restes', 'cause', 'causes',
  'passe', 'passes', 'passee', 'passees',
  'blanc', 'blancs', 'blanche', 'blanches', 'rouge', 'rouges',
  'jaune', 'jaunes', 'coeur', 'coeurs', 'corps', 'esprit', 'esprits',
  'aider', 'guide', 'guides', 'guidee', 'guidees',
  'valeur', 'valeurs', 'faille', 'failles', 'onze', 'radio', 'radios',
  'morse', 'signal', 'signaux', 'ombre', 'ombres', 'brave', 'braves', 'magie', 'magies',
  // Verbes conjugués 5 lettres
  'aimer', 'aimes', 'aimee', 'aimees', 'aller', 'allez', 'anime', 'animes', 'animee', 'animees',
  'avoir', 'batir', 'boire', 'casse', 'casses', 'cassee', 'cassees',
  'ceder', 'colle', 'colles', 'collee', 'collees', 'coupe', 'coupes', 'coupee', 'coupees',
  'coure', 'coule', 'coules', 'coulee', 'coulees', 'court', 'courts', 'courte', 'courtes',
  'creer', 'crier', 'cuire', 'dance', 'dances', 'dansee', 'dansees',
  'diner', 'donne', 'donnes', 'donnee', 'donnees', 'doute', 'doutes', 'doutee', 'doutees',
  'durer', 'ecrire', 'eleve', 'eleves', 'elevee', 'elevees',
  'enfin', 'envie', 'envies', 'enviee', 'enviees', 'essai', 'essais',
  'etait', 'etude', 'etudes', 'faire', 'ferme', 'fermes', 'fermee', 'fermees',
  'filer', 'finir', 'fixer', 'fuire', 'geler', 'gerer',
  'goute', 'goutes', 'goutee', 'goutees', 'jouer', 'jurer',
  'lacer', 'laver', 'lever', 'limer', 'loger', 'louer', 'luire',
  'meler', 'mener', 'mimer', 'miser', 'muter', 'nager', 'noter', 'nouer', 'nuire',
  'obeit', 'offre', 'offres', 'offert', 'offerts', 'offerte', 'offertes',
  'orner', 'paier', 'parle', 'parles', 'parlee', 'parlees',
  'pause', 'pauses', 'payer', 'peser', 'piler', 'plier', 'poser', 'prier',
  'prend', 'ramer', 'raser', 'rater', 'ravir',
  'reagir', 'remer', 'remet', 'rimer', 'roder', 'roule', 'roules', 'roulee', 'roulees',
  'ruser', 'saler', 'sauce', 'sauces', 'saucee', 'saucees',
  'saute', 'sautes', 'sautee', 'sautees', 'semer', 'serai',
  'signe', 'signes', 'signee', 'signees', 'songe', 'songes', 'songee', 'songees',
  'sorte', 'sortes', 'soude', 'soudes', 'soudee', 'soudees',
  'sucer', 'sumer', 'taire', 'taper', 'tarer', 'tarir', 'taxer',
  'tenir', 'teste', 'testes', 'testee', 'testees', 'tient',
  'tirer', 'tombe', 'tombes', 'tombee', 'tombees', 'torde',
  'trace', 'traces', 'tracee', 'tracees', 'train', 'trains',
  'trouv', 'tuera', 'ultra', 'ultras', 'unira', 'usera',
  'venir', 'verse', 'verses', 'versee', 'versees',
  'vibre', 'vibres', 'vibree', 'vibrees', 'vider', 'vient',
  'viole', 'violes', 'violee', 'violees', 'viser', 'vivre', 'voila',
  'voler', 'vomir', 'voter', 'vouer',
  // 6 lettres (avec pluriels et féminins)
  'encore', 'moment', 'moments', 'groupe', 'groupes', 'nombre', 'nombres',
  'niveau', 'niveaux', 'milieu', 'milieux', 'action', 'actions',
  'projet', 'projets', 'marche', 'marches', 'mesure', 'mesures',
  'centre', 'centres', 'raison', 'raisons', 'regard', 'regards',
  'besoin', 'besoins', 'sortie', 'sorties', 'entree', 'entrees',
  'source', 'sources', 'nature', 'natures', 'valeur', 'valeurs',
  'parent', 'parents', 'parente', 'parentes', 'classe', 'classes',
  'equipe', 'equipes', 'saison', 'saisons', 'chance', 'chances',
  'danger', 'dangers', 'avenir', 'avenirs', 'espoir', 'espoirs',
  'ecoute', 'ecoutes', 'partage', 'partages', 'soutien', 'soutiens',
  'couleur', 'couleurs', 'couler', 'amitie', 'amities',
  'calmer', 'parler', 'donner', 'sentir', 'grandir', 'sourire',
  'relier', 'jouer', 'aimer', 'entier', 'entiers', 'entiere', 'entieres',
  'sortir', 'porter', 'mettre', 'prendre', 'croire', 'savoir',
  'pouvoir', 'vouloir', 'devoir', 'partir', 'venir', 'tenir',
  'mourir', 'ouvrir', 'offrir', 'dormir', 'servir', 'suivre', 'vivre', 'ecrire',
  'battre', 'perdre', 'rendre', 'vendre', 'repondre', 'attendre', 'entendre', 'descendre',
  'confier', 'eduquer', 'ecouter', 'partager',
  // 7 lettres (avec pluriels et féminins)
  'travail', 'travaux', 'service', 'services', 'pouvoir', 'pouvoirs',
  'famille', 'familles', 'courage', 'courages', 'emotion', 'emotions',
  'respect', 'respects', 'patience', 'patiences', 'lumiere', 'lumieres',
  'portail', 'portails', 'monstre', 'monstres', 'victoire', 'victoires',
  'energie', 'energies', 'attaque', 'attaques', 'empathie', 'empathies',
  'tendresse', 'tendresses', 'confiance', 'confiances', 'entraide', 'entraides',
  'soutenir', 'grandir', 'sourire', 'confier', 'eduquer', 'ecouter', 'partager',
  'trouver', 'chercher', 'demander', 'repondre', 'regarder', 'entendre', 'attendre',
  'apprendre', 'comprendre', 'connaitre', 'reconnaitre',
  // 8+ lettres (avec pluriels)
  'toujours', 'quelque', 'quelques', 'question', 'questions',
  'bienveillance', 'bienveillances', 'tolerance', 'tolerances',
  'solidarite', 'solidarites', 'autonomie', 'autonomies',
  'independance', 'independances', 'responsabilite', 'responsabilites',
  'accompagner', 'comprendre', 'expliquer', 'apprendre', 'enseigner', 'progresser',
]);

// Combiner les deux dictionnaires
const ALL_WORDS = new Set([...FRENCH_DICTIONARY, ...THEMATIC_WORDS]);

// Calcul du score basé sur la longueur du mot
function getWordScore(word: string): number {
  const len = word.length;
  if (len <= 2) return 0; // Mots trop courts
  if (len === 3) return 1;
  if (len === 4) return 1;
  if (len === 5) return 2;
  if (len === 6) return 3;
  if (len === 7) return 5;
  return 11; // 8+ lettres
}

// Bonus pour mots thématiques
function getThematicBonus(word: string): number {
  return THEMATIC_WORDS.has(word.toLowerCase()) ? 2 : 0;
}

// Directions adjacentes pour le Boggle
const DIRECTIONS = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1],          [0, 1],
  [1, -1],  [1, 0], [1, 1]
];

// Trouver un chemin valide pour placer un mot dans la grille
function findPathForWord(
  grid: (string | null)[][],
  word: string,
  startRow: number,
  startCol: number,
  visited: Set<string>
): [number, number][] | null {
  if (word.length === 0) return [];

  const key = `${startRow},${startCol}`;
  if (visited.has(key)) return null;
  if (startRow < 0 || startRow >= 4 || startCol < 0 || startCol >= 4) return null;

  const currentCell = grid[startRow][startCol];
  const targetLetter = word[0].toUpperCase();

  // Si la cellule a déjà une lettre, elle doit correspondre
  if (currentCell !== null && currentCell !== targetLetter) return null;

  if (word.length === 1) {
    return [[startRow, startCol]];
  }

  const newVisited = new Set(visited);
  newVisited.add(key);

  // Essayer toutes les directions
  const shuffledDirs = [...DIRECTIONS].sort(() => Math.random() - 0.5);
  for (const [dr, dc] of shuffledDirs) {
    const newRow = startRow + dr;
    const newCol = startCol + dc;
    const path = findPathForWord(grid, word.slice(1), newRow, newCol, newVisited);
    if (path) {
      return [[startRow, startCol], ...path];
    }
  }

  return null;
}

// Placer un mot dans la grille
function placeWordInGrid(grid: (string | null)[][], word: string): boolean {
  const upperWord = word.toUpperCase();

  // Essayer de commencer depuis différentes positions
  const positions: [number, number][] = [];
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      positions.push([r, c]);
    }
  }

  // Mélanger les positions pour la variété
  positions.sort(() => Math.random() - 0.5);

  for (const [startRow, startCol] of positions) {
    const path = findPathForWord(grid, upperWord, startRow, startCol, new Set());
    if (path) {
      // Placer les lettres
      for (let i = 0; i < path.length; i++) {
        const [r, c] = path[i];
        grid[r][c] = upperWord[i];
      }
      return true;
    }
  }

  return false;
}

// Remplir les cases vides avec des lettres favorables
function fillEmptyCells(grid: (string | null)[][]): string[][] {
  const result: string[][] = [];

  // Compter les voyelles et consonnes déjà présentes
  let vowelCount = 0;
  let consonantCount = 0;
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      if (grid[r][c]) {
        if ('AEIOU'.includes(grid[r][c]!)) vowelCount++;
        else consonantCount++;
      }
    }
  }

  // On veut environ 6 voyelles et 10 consonnes
  const targetVowels = 6;
  const targetConsonants = 10;

  for (let r = 0; r < 4; r++) {
    const row: string[] = [];
    for (let c = 0; c < 4; c++) {
      if (grid[r][c]) {
        row.push(grid[r][c]!);
      } else {
        // Choisir une voyelle ou consonne selon les besoins
        const needMoreVowels = vowelCount < targetVowels;
        const needMoreConsonants = consonantCount < targetConsonants;

        let letter: string;
        if (needMoreVowels && (!needMoreConsonants || Math.random() < 0.4)) {
          letter = COMMON_VOWELS[Math.floor(Math.random() * COMMON_VOWELS.length)];
          vowelCount++;
        } else {
          letter = COMMON_CONSONANTS[Math.floor(Math.random() * COMMON_CONSONANTS.length)];
          consonantCount++;
        }
        row.push(letter);
      }
    }
    result.push(row);
  }

  return result;
}

// Générer une grille avec des mots thématiques garantis ET au moins un mot long
function generateGrid(): string[][] {
  const MIN_WORDS = 15;
  const MIN_THEMATIC = 3;
  const MAX_ATTEMPTS = 50;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    // Créer une grille vide
    const grid: (string | null)[][] = Array(4).fill(null).map(() => Array(4).fill(null));

    const placedWords: string[] = [];

    // D'ABORD: Essayer de placer 2 mots LONGS (6+ lettres)
    const shuffledLong = [...PLACEABLE_WORDS_LONG].sort(() => Math.random() - 0.5);
    let placedLongCount = 0;
    for (const word of shuffledLong) {
      if (placedLongCount >= 2) break; // Max 2 mots longs
      if (placeWordInGrid(grid, word)) {
        placedWords.push(word);
        placedLongCount++;
      }
    }

    // ENSUITE: Ajouter des mots courts pour remplir
    const shuffledShort = [...PLACEABLE_WORDS_SHORT].sort(() => Math.random() - 0.5);
    for (const word of shuffledShort) {
      if (placedWords.length >= 5) break;
      if (placeWordInGrid(grid, word)) {
        placedWords.push(word);
      }
    }

    // Si on n'a pas pu placer assez de mots, réessayer
    if (placedWords.length < 2) continue;

    // Remplir les cases vides
    const filledGrid = fillEmptyCells(grid);

    // Valider la grille
    const validWords = findAllValidWordsQuick(filledGrid);
    const thematicCount = validWords.filter(w => THEMATIC_WORDS.has(w)).length;
    const longWordsCount = validWords.filter(w => w.length >= 6).length;

    // Exiger au moins un mot de 6+ lettres trouvable
    if (validWords.length >= MIN_WORDS && thematicCount >= MIN_THEMATIC && longWordsCount >= 1) {
      const longWords = validWords.filter(w => w.length >= 6);
      console.log(`✅ Grille générée en ${attempt + 1} essais`);
      console.log(`   Mots placés: ${placedWords.join(', ')}`);
      console.log(`   Mots longs disponibles (6+): ${longWords.join(', ')}`);
      return filledGrid;
    }
  }

  // Fallback: générer une grille avec distribution optimale de lettres
  console.log('⚠️ Utilisation de la grille de secours');
  return generateFallbackGrid();
}

// Grille de secours avec une bonne distribution de lettres
function generateFallbackGrid(): string[][] {
  // Distribution optimale : beaucoup de E, A, I, S, R, T, N
  const goodLetters = [
    'E', 'A', 'I', 'O', 'U',  // voyelles
    'E', 'A', 'I',            // voyelles bonus
    'S', 'R', 'T', 'N', 'L',  // consonnes fréquentes
    'D', 'C', 'M'             // consonnes communes
  ];

  const shuffled = [...goodLetters].sort(() => Math.random() - 0.5);
  const grid: string[][] = [];

  for (let r = 0; r < 4; r++) {
    const row: string[] = [];
    for (let c = 0; c < 4; c++) {
      row.push(shuffled[r * 4 + c]);
    }
    grid.push(row);
  }

  return grid;
}

// Version rapide de findAllValidWords pour la génération
function findAllValidWordsQuick(grid: string[][]): string[] {
  const validWords: Set<string> = new Set();

  function dfs(row: number, col: number, path: [number, number][], currentWord: string) {
    const wordLower = currentWord.toLowerCase();
    if (currentWord.length >= 3 && ALL_WORDS.has(wordLower)) {
      validWords.add(wordLower);
    }
    if (currentWord.length >= 9) return; // Explorer jusqu'à 9 lettres

    for (const [dr, dc] of DIRECTIONS) {
      const newRow = row + dr;
      const newCol = col + dc;
      if (newRow < 0 || newRow >= 4 || newCol < 0 || newCol >= 4) continue;
      if (path.some(([r, c]) => r === newRow && c === newCol)) continue;
      dfs(newRow, newCol, [...path, [newRow, newCol]], currentWord + grid[newRow][newCol]);
    }
  }

  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      dfs(row, col, [[row, col]], grid[row][col]);
    }
  }

  return Array.from(validWords);
}

// Vérifier si deux cellules sont adjacentes
function areAdjacent(pos1: [number, number], pos2: [number, number]): boolean {
  const rowDiff = Math.abs(pos1[0] - pos2[0]);
  const colDiff = Math.abs(pos1[1] - pos2[1]);
  return rowDiff <= 1 && colDiff <= 1 && (rowDiff !== 0 || colDiff !== 0);
}

// Trouver tous les mots valides dans une grille
function findAllValidWords(grid: string[][]): string[] {
  const validWords: Set<string> = new Set();

  function dfs(row: number, col: number, path: [number, number][], currentWord: string) {
    const wordLower = currentWord.toLowerCase();

    // Si le mot est valide et assez long, l'ajouter
    if (currentWord.length >= 3 && ALL_WORDS.has(wordLower)) {
      validWords.add(wordLower);
    }

    // Limiter la profondeur de recherche
    if (currentWord.length >= 9) return;

    // Explorer les cellules adjacentes
    for (const [dr, dc] of DIRECTIONS) {
      const newRow = row + dr;
      const newCol = col + dc;

      // Vérifier les limites
      if (newRow < 0 || newRow >= 4 || newCol < 0 || newCol >= 4) continue;

      // Vérifier si déjà visité dans ce chemin
      if (path.some(([r, c]) => r === newRow && c === newCol)) continue;

      const newPath: [number, number][] = [...path, [newRow, newCol]];
      const newWord = currentWord + grid[newRow][newCol];
      dfs(newRow, newCol, newPath, newWord);
    }
  }

  // Démarrer DFS depuis chaque cellule
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      dfs(row, col, [[row, col]], grid[row][col]);
    }
  }

  // Trier par longueur puis alphabétiquement
  return Array.from(validWords).sort((a, b) => {
    if (b.length !== a.length) return b.length - a.length;
    return a.localeCompare(b);
  });
}

export function BogglePuzzle({
  isOpen,
  onClose,
  onSuccess,
  targetScore,
  timeLimit,
}: BogglePuzzleProps) {
  const [grid, setGrid] = useState<string[][]>([]);
  const [selectedCells, setSelectedCells] = useState<[number, number][]>([]);
  const [currentWord, setCurrentWord] = useState('');
  const [foundWords, setFoundWords] = useState<Set<string>>(new Set());
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [isGameOver, setIsGameOver] = useState(false);
  const [isVictory, setIsVictory] = useState(false);
  const [lastWordStatus, setLastWordStatus] = useState<'valid' | 'invalid' | 'duplicate' | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  // Initialiser le jeu
  useEffect(() => {
    if (isOpen) {
      const newGrid = generateGrid();
      setGrid(newGrid);
      setSelectedCells([]);
      setCurrentWord('');
      setFoundWords(new Set());
      setScore(0);
      setTimeLeft(timeLimit);
      setIsGameOver(false);
      setIsVictory(false);
      setLastWordStatus(null);

      // DEBUG: Afficher les mots possibles dans la console
      const validWords = findAllValidWords(newGrid);
      const totalPossibleScore = validWords.reduce((acc, word) => {
        return acc + getWordScore(word) + getThematicBonus(word);
      }, 0);
      console.log('🎲 Grille générée:', newGrid.map(row => row.join(' ')).join('\n'));
      console.log(`📝 ${validWords.length} mots possibles (score total: ${totalPossibleScore}):`);
      console.log('Mots par longueur:');
      const byLength: Record<number, string[]> = {};
      validWords.forEach(w => {
        const len = w.length;
        if (!byLength[len]) byLength[len] = [];
        byLength[len].push(w);
      });
      Object.keys(byLength).sort((a, b) => Number(b) - Number(a)).forEach(len => {
        const words = byLength[Number(len)];
        const thematic = words.filter(w => THEMATIC_WORDS.has(w));
        console.log(`  ${len} lettres (${words.length}): ${words.slice(0, 15).join(', ')}${words.length > 15 ? '...' : ''}`);
        if (thematic.length > 0) {
          console.log(`    ★ Thématiques: ${thematic.join(', ')}`);
        }
      });
    }
  }, [isOpen, timeLimit]);

  // Timer
  useEffect(() => {
    if (!isOpen || isGameOver) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsGameOver(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, isGameOver]);

  // Vérifier la victoire
  useEffect(() => {
    if (score >= targetScore && !isVictory) {
      setIsVictory(true);
      setIsGameOver(true);
      setTimeout(() => {
        onSuccess();
      }, 2000);
    }
  }, [score, targetScore, isVictory, onSuccess]);

  // Gestion de la sélection de cellules
  const handleCellSelect = useCallback((row: number, col: number) => {
    if (isGameOver) return;

    const pos: [number, number] = [row, col];

    // Vérifier si la cellule est déjà sélectionnée
    const existingIndex = selectedCells.findIndex(
      ([r, c]) => r === row && c === col
    );

    if (existingIndex !== -1) {
      // Désélectionner si c'est la dernière cellule
      if (existingIndex === selectedCells.length - 1 && selectedCells.length > 1) {
        const newSelected = selectedCells.slice(0, -1);
        setSelectedCells(newSelected);
        setCurrentWord(newSelected.map(([r, c]) => grid[r][c]).join(''));
      }
      return;
    }

    // Vérifier l'adjacence avec la dernière cellule sélectionnée
    if (selectedCells.length > 0) {
      const lastCell = selectedCells[selectedCells.length - 1];
      if (!areAdjacent(lastCell, pos)) {
        return;
      }
    }

    // Ajouter la cellule
    const newSelected = [...selectedCells, pos];
    setSelectedCells(newSelected);
    setCurrentWord(newSelected.map(([r, c]) => grid[r][c]).join(''));
  }, [selectedCells, grid, isGameOver]);

  // Soumettre le mot
  const submitWord = useCallback(() => {
    if (currentWord.length < 3) {
      setLastWordStatus('invalid');
      setTimeout(() => setLastWordStatus(null), 500);
      setSelectedCells([]);
      setCurrentWord('');
      return;
    }

    const wordLower = currentWord.toLowerCase();

    if (foundWords.has(wordLower)) {
      setLastWordStatus('duplicate');
      setTimeout(() => setLastWordStatus(null), 500);
      setSelectedCells([]);
      setCurrentWord('');
      return;
    }

    if (ALL_WORDS.has(wordLower)) {
      const baseScore = getWordScore(wordLower);
      const bonus = getThematicBonus(wordLower);
      const totalScore = baseScore + bonus;

      setScore((prev) => prev + totalScore);
      setFoundWords((prev) => new Set([...prev, wordLower]));
      setLastWordStatus('valid');
    } else {
      setLastWordStatus('invalid');
    }

    setTimeout(() => setLastWordStatus(null), 500);
    setSelectedCells([]);
    setCurrentWord('');
  }, [currentWord, foundWords]);

  // Annuler la sélection
  const clearSelection = useCallback(() => {
    setSelectedCells([]);
    setCurrentWord('');
  }, []);

  // Ref pour tracker si on a fait un drag (vs un simple clic)
  const hasDraggedRef = useRef(false);
  const pointerStartRef = useRef<{ row: number; col: number } | null>(null);

  // Gestion du clic sur une cellule
  const handleCellClick = useCallback((row: number, col: number) => {
    // Si on vient de faire un drag, ignorer le click
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }
    if (isGameOver) return;

    const pos: [number, number] = [row, col];

    // Vérifier si la cellule est déjà sélectionnée
    const existingIndex = selectedCells.findIndex(
      ([r, c]) => r === row && c === col
    );

    if (existingIndex !== -1) {
      // Si c'est la dernière cellule, la désélectionner
      if (existingIndex === selectedCells.length - 1) {
        const newSelected = selectedCells.slice(0, -1);
        setSelectedCells(newSelected);
        setCurrentWord(newSelected.map(([r, c]) => grid[r][c]).join(''));
      }
      return;
    }

    // Si c'est la première cellule, l'ajouter directement
    if (selectedCells.length === 0) {
      setSelectedCells([pos]);
      setCurrentWord(grid[row][col]);
      return;
    }

    // Vérifier l'adjacence avec la dernière cellule sélectionnée
    const lastCell = selectedCells[selectedCells.length - 1];
    if (!areAdjacent(lastCell, pos)) {
      setLastWordStatus('invalid');
      setTimeout(() => setLastWordStatus(null), 300);
      return;
    }

    // Ajouter la cellule
    const newSelected = [...selectedCells, pos];
    setSelectedCells(newSelected);
    setCurrentWord(newSelected.map(([r, c]) => grid[r][c]).join(''));
  }, [selectedCells, grid, isGameOver]);

  // Gestion du drag (pour mobile et desktop)
  const handlePointerDown = useCallback((row: number, col: number, e: React.PointerEvent) => {
    if (isGameOver) return;
    e.preventDefault();
    setIsSelecting(true);
    hasDraggedRef.current = false;
    pointerStartRef.current = { row, col };
  }, [isGameOver]);

  const handlePointerEnter = useCallback((row: number, col: number) => {
    if (!isSelecting || isGameOver) return;

    // Marquer qu'on a fait un drag (pas un simple clic)
    if (pointerStartRef.current && (pointerStartRef.current.row !== row || pointerStartRef.current.col !== col)) {
      hasDraggedRef.current = true;
    }

    const pos: [number, number] = [row, col];

    // Si c'est la première cellule du drag
    if (selectedCells.length === 0 || !hasDraggedRef.current) {
      // On n'est pas encore en mode drag, juste en attente
      if (hasDraggedRef.current) {
        // Commencer le drag avec la cellule de départ
        const startPos: [number, number] = [pointerStartRef.current!.row, pointerStartRef.current!.col];
        if (!selectedCells.some(([r, c]) => r === startPos[0] && c === startPos[1])) {
          setSelectedCells([startPos]);
          setCurrentWord(grid[startPos[0]][startPos[1]]);
        }
      }
    }

    // Vérifier si déjà sélectionné
    if (selectedCells.some(([r, c]) => r === row && c === col)) {
      return;
    }

    // Vérifier l'adjacence
    if (selectedCells.length > 0) {
      const lastCell = selectedCells[selectedCells.length - 1];
      if (!areAdjacent(lastCell, pos)) {
        return;
      }
    }

    // Ajouter la cellule
    const newSelected = selectedCells.length === 0 ? [pos] : [...selectedCells, pos];
    setSelectedCells(newSelected);
    setCurrentWord(newSelected.map(([r, c]) => grid[r][c]).join(''));
  }, [isSelecting, selectedCells, grid, isGameOver]);

  const handlePointerUp = useCallback(() => {
    if (!isSelecting) return;

    // Si on a fait un drag avec au moins 3 lettres, soumettre
    if (hasDraggedRef.current && selectedCells.length >= 3) {
      submitWord();
    }

    setIsSelecting(false);
    pointerStartRef.current = null;
  }, [isSelecting, selectedCells, submitWord]);

  // Event listeners globaux pour le pointer up
  useEffect(() => {
    const handleGlobalPointerUp = () => {
      handlePointerUp();
    };

    window.addEventListener('pointerup', handleGlobalPointerUp);
    window.addEventListener('pointercancel', handleGlobalPointerUp);
    return () => {
      window.removeEventListener('pointerup', handleGlobalPointerUp);
      window.removeEventListener('pointercancel', handleGlobalPointerUp);
    };
  }, [handlePointerUp]);

  if (!isOpen) return null;

  const progressPercent = Math.min((score / targetScore) * 100, 100);

  // Empêcher la propagation des clics vers le parent (riddle-overlay)
  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="boggle-overlay" onClick={handleContainerClick} onPointerDown={handleContainerClick}>
      <div className={`boggle-container ${isVictory ? 'victory' : ''} ${isGameOver && !isVictory ? 'defeat' : ''}`} onClick={handleContainerClick}>
        {/* Header */}
        <div className="boggle-header">
          <div className="boggle-title">
            <span className="boggle-emoji">📝</span>
            <span>LaBog du Demagordon</span>
          </div>
          <button className="boggle-close" onClick={onClose}>✕</button>
        </div>

        {/* Stats */}
        <div className="boggle-stats">
          <div className="boggle-timer">
            <span className="stat-icon">⏱️</span>
            <span className={timeLeft <= 10 ? 'urgent' : ''}>{timeLeft}s</span>
          </div>
          <div className="boggle-score">
            <span className="stat-icon">⭐</span>
            <span>{score}/{targetScore}</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="boggle-progress">
          <div
            className="boggle-progress-bar"
            style={{ width: `${progressPercent}%` }}
          />
          {progressPercent >= 100 && <span className="progress-complete">VICTOIRE!</span>}
        </div>

        {/* Current word display */}
        <div className={`boggle-current-word ${lastWordStatus || ''}`}>
          {currentWord || '...'}
        </div>

        {/* Grid */}
        <div
          className="boggle-grid"
          ref={gridRef}
          onPointerLeave={() => isSelecting && handlePointerUp()}
        >
          {grid.map((row, rowIndex) => (
            <div key={rowIndex} className="boggle-row">
              {row.map((letter, colIndex) => {
                const isSelected = selectedCells.some(
                  ([r, c]) => r === rowIndex && c === colIndex
                );
                const selectionIndex = selectedCells.findIndex(
                  ([r, c]) => r === rowIndex && c === colIndex
                );

                return (
                  <div
                    key={colIndex}
                    className={`boggle-cell ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                    onPointerDown={(e) => handlePointerDown(rowIndex, colIndex, e)}
                    onPointerEnter={() => handlePointerEnter(rowIndex, colIndex)}
                    style={isSelected ? { '--selection-order': selectionIndex } as React.CSSProperties : undefined}
                  >
                    <span className="cell-letter">{letter}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="boggle-actions">
          <button
            className="boggle-btn clear"
            onClick={clearSelection}
            disabled={isGameOver}
          >
            🔄 Effacer
          </button>
          <button
            className="boggle-btn submit"
            onClick={submitWord}
            disabled={isGameOver || currentWord.length < 3}
          >
            ✓ Valider
          </button>
        </div>

        {/* Found words */}
        <div className="boggle-found-words">
          <div className="found-words-title">
            <span>Mots trouvés ({foundWords.size})</span>
          </div>
          <div className="found-words-list">
            {Array.from(foundWords)
              .sort((a, b) => b.length - a.length) // Trier par longueur décroissante
              .map((word) => {
                const isThematic = THEMATIC_WORDS.has(word);
                const isLong = word.length >= 6;
                const pts = getWordScore(word) + getThematicBonus(word);
                return (
                  <span
                    key={word}
                    className={`found-word ${isThematic ? 'thematic' : ''} ${isLong ? 'long-word' : ''}`}
                  >
                    {word}
                    <span className="word-points">+{pts}</span>
                    {isThematic && <span className="bonus-star">★</span>}
                  </span>
                );
              })}
          </div>
        </div>

        {/* Game over overlay */}
        {isGameOver && (
          <div className="boggle-game-over">
            {isVictory ? (
              <>
                <div className="game-over-icon victory">🎉</div>
                <div className="game-over-text">VICTOIRE!</div>
                <div className="game-over-subtext">
                  Tu as vaincu le Demagordon avec {score} points!
                </div>
                <div className="game-over-subtext cotelette">
                  Cotelette prépare son attaque...
                </div>
              </>
            ) : (
              <>
                <div className="game-over-icon defeat">💀</div>
                <div className="game-over-text">TEMPS ÉCOULÉ!</div>
                <div className="game-over-subtext">
                  Score: {score}/{targetScore}
                </div>
                <button className="boggle-retry" onClick={() => {
                  setGrid(generateGrid());
                  setSelectedCells([]);
                  setCurrentWord('');
                  setFoundWords(new Set());
                  setScore(0);
                  setTimeLeft(timeLimit);
                  setIsGameOver(false);
                  setIsVictory(false);
                }}>
                  🔄 Réessayer
                </button>
              </>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="boggle-instructions">
          <p>Glisse sur les lettres adjacentes pour former des mots!</p>
          <p>★ = Bonus mots thématiques (+2 pts)</p>
        </div>
      </div>
    </div>
  );
}
